import api from "APP_ROOT/api";
import { createChannel } from "APP_ROOT/cable";
import { push } from "react-router-redux";
import { SUCCESS } from "LIB/submission";
import { actionFactory } from "@orange-marmalade/paginate-this";
import * as subscriptionActionTypes from "MODULES/Subscription/actionTypes";
import { CATALOG, PART } from "MODULES/parts/constants";
import { ID } from "APP_ROOT/constants";
import { toastr } from "react-redux-toastr";
import * as actionTypes from "../actionTypes";
import { partsPagination } from "../pagination";

export * as incomingQuoteActions from "./incomingQuotes";
export * as outgoingQuoteActions from "./outgoingQuotes";
export * as managedRepairOrderActions from "./managedRepairOrders";
export * as repairOrderActions from "./repairOrders";
export * as salesOrderActions from "./salesOrders";
export const partsAlternatesActions = actionFactory("part/alternates");

export function create(part) {
  return dispatch =>
    api.parts.create(part).then(({ data }) => {
      dispatch({
        type: actionTypes.SET,
        part: data
      });
      dispatch(push(`/parts/${data.uuid}`));
    });
}

export function show(id) {
  return dispatch =>
    api.parts.show(id).then(resp =>
      dispatch({
        type: actionTypes.SET,
        part: resp.data
      })
    );
}

export function searchPartsByStatus(filter) {
  return () =>
    api.parts
      .search({
        part_number: filter,
        repair_status: "reserve_for_repair,reserve_for_shop_assist",
        search_type: "search"
      })
      .then(resp => resp.data.results);
}

function createCatalogUrl(file) {
  if (!file) {
    return Promise.resolve(null);
  }

  const fileName = `${+new Date() -
    Math.floor(Math.random() * 10000000000)}${file.name}`;

  return api.attachments.upload(fileName).then(resp => {
    const { key, url } = resp.data;

    return api.attachments
      .toS3(url, file)
      .then(() => Promise.resolve(key))
      .catch(e => Promise.reject(e));
  });
}

export function update(data) {
  return (dispatch, getState) => {
    const partID = getState().partDetail.getIn([PART, ID]);

    return createCatalogUrl(data.file).then(catalogKey => {
      if (catalogKey) {
        data[CATALOG] = catalogKey;
        data.file = null;
      }

      return api.parts.update(partID, data).then(resp => {
        dispatch({
          type: actionTypes.SET,
          part: resp.data
        });
        dispatch(partsPagination.pageActions.expire());
        dispatch({
          type: subscriptionActionTypes.SUBSCRIPTION_NOTIFICATION,
          Inventory: {
            stale: true
          }
        });
        return SUCCESS;
      });
    });
  };
}

export function searchInventories(partId, filters = {}) {
  return () =>
    api.parts.inventories
      .search(partId, filters)
      .then(resp => resp.data.results);
}

export function searchOutgoingQuoteLineItems(partId) {
  return () =>
    api.parts.outgoingQuoteLineItems
      .search(partId)
      .then(resp => resp.data.results);
}

export function searchIncomingQuoteLineItems(partId) {
  return () =>
    api.parts.incomingQuoteLineItems
      .search(partId)
      .then(resp => resp.data.results);
}

export function searchParts(filter) {
  return () =>
    api.parts
      .search({
        part_number: filter,
        search_type: "search"
      })
      .then(resp => resp.data.results);
}

export function searchPoParts(filter) {
  return () =>
    api.parts
      .poPartSearch({ part_number: filter })
      .then(resp => resp.data.results);
}

export function searchMatchingPartNumbers(partNumber) {
  return () =>
    api.parts
      .search({
        part_number: partNumber,
        search_type: "matching_part_number",
        limit: 2
      })
      .then(resp => resp.data.results);
}

export function createAlternate(id, data) {
  return dispatch =>
    api.parts.alternates.create(id, data).then(() => {
      dispatch(partsAlternatesActions.expire());
      dispatch(show(id));
      return SUCCESS;
    });
}

export function updateAlternate(id, data) {
  return dispatch =>
    api.alternates.update(id, data).then(resp => {
      dispatch(partsAlternatesActions.expire());
      dispatch(show(resp.data.part_id));
      return SUCCESS;
    });
}

export function deleteAlternate(id) {
  return dispatch =>
    api.alternates
      .delete(id)
      .then(() => dispatch(partsAlternatesActions.expire()));
}

export function searchCompanies() {
  return name => () =>
    api.companies
      .search({
        filter: name,
        status: "active,do_not_do_business",
        sort: "name",
        reverse: false,
        limit: 20
      })
      .then(resp => resp.data.results);
}

export function searchQuoteCompanies() {
  return name => () =>
    api.companies
      .search({
        cached: true,
        filter: name,
        status: "active,credit_hold,do_not_do_business",
        sort: "name",
        reverse: false,
        limit: 20
      })
      .then(resp => resp.data.results);
}

export function searchPartNumber() {
  return partNumber => () =>
    api.parts
      .search({
        filter: partNumber,
        limit: 6
      })
      .then(resp => resp.data.results);
}

export function globalPartsSearch(filter) {
  return () =>
    api.parts
      .search({ filter, sort: "base_part_number", reverse: false, limit: 15 })
      .then(resp => resp.data.results);
}

export function searchContacts(companyId) {
  return api.companies.contacts
    .search(companyId, {
      active: true,
      cached: true,
      page: 1,
      limit: 1000,
      sort: "name",
      reverse: false
    })
    .then(resp => Promise.resolve(resp.data.results));
}

export function subscribe(partId) {
  return dispatch =>
    createChannel().listen(
      { channel: "PartsChannel", id: partId },
      {
        onReceive: data => {
          dispatch({
            type: subscriptionActionTypes.SUBSCRIPTION_NOTIFICATION,
            [data]: {
              stale: true
            }
          });
        }
      }
    );
}

export function importParts(values) {
  return () => {
    const fileName = values.file.name;

    return api.attachments.upload(values.file.name).then(resp => {
      const { key, url } = resp.data;
      const formattedAttachment = {
        name: fileName,
        key
      };

      return api.attachments
        .toS3(url, values.file)
        .then(() =>
          api.parts.import(formattedAttachment).then(() => {
            toastr.info("Processing", "Importing Parts.");
            return SUCCESS;
          })
        )
        .catch(e => Promise.reject(e));
    });
  };
}



// WEBPACK FOOTER //
// ./app/modules/parts/actions/actions.js